import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import { ForceSelectionAutoCompleteField, Option } from '../../../core/components/Fields/AutoCompleteField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { GET_TEAMS_CHANNELS } from '../queries.ts';

// Reasons the backend reports instead of a channel list (integrations/teams/schema.py).
const REASON_KEYS = {
    not_connected: 'form.summaryTeamsNotConnected',
    permission_required: 'form.summaryTeamsPermissionRequired',
};

const channelLabel = (teamName: string, channelName: string) => `${teamName} / ${channelName}`;

const renderChannelOption = (option: Option) => (
    <span>
        <span>{option.label}</span>
        <span className="block text-xs text-content-weak">{option.description}</span>
    </span>
);

/**
 * Type-ahead over the channels of the connected Teams account (user profile → Microsoft Teams), picking the one
 * the call summaries are posted into.
 */
const TeamsChannelPicker = ({ form, disabled = false }) => {
    const { t } = useTranslation('voice_agents');

    // listing the channels hits Microsoft Graph — only do it once the channel is actually switched on. While it
    // is off the field offers nothing at all: without options AND without an empty message the suggestion
    // popover cannot open, not even through the field's own (never disabled) dropdown button.
    const { data, loading } = useQuery(GET_TEAMS_CHANNELS, { skip: disabled });
    const teams = disabled ? [] : data?.teamsChannels?.teams || [];
    const error = disabled ? '' : data?.teamsChannels?.error || '';

    const teamName: string = form.watch('summaryTeamsTeamName');
    const channelName: string = form.watch('summaryTeamsChannelName');
    const hasChannel: boolean = Boolean(form.watch('summaryTeamsChannelId'));

    // the option value is what the input shows and what force-selection matches on, so it spells out both names;
    // the ids ride along and are picked up on selection
    const options: Option[] = useMemo(
        () =>
            teams.flatMap((team) =>
                (team.channels || []).map((channel) => ({
                    value: channelLabel(team.name, channel.name),
                    label: channel.name,
                    description: team.name,
                    teamId: team.id,
                    channelId: channel.id,
                }))
            ),
        [teams]
    );

    const selectChannel = (option?: Option | null) => {
        form.setValue('summaryTeamsTeamId', option?.teamId || '', { shouldDirty: true });
        form.setValue('summaryTeamsTeamName', option?.description || '', { shouldDirty: true });
        form.setValue('summaryTeamsChannelName', option?.label || '', { shouldDirty: true });
    };

    const reason = error ? t(REASON_KEYS[error] || 'form.summaryTeamsUnavailable') : '';
    const helpText =
        reason ||
        (hasChannel
            ? t('form.summaryTeamsSelected', { channel: channelLabel(teamName, channelName) })
            : t('form.summaryTeamsChannelHelpText'));

    return (
        <Controller
            name="summaryTeamsChannelId"
            control={form.control}
            rules={{
                validate: (value: string) =>
                    !form.watch('summaryTeamsEnabled') ||
                    Boolean(value) ||
                    (t('form.summaryTeamsChannelRequiredError') as string),
            }}
            render={({ field }) => (
                <FormField
                    label={t('form.summaryTeamsChannel')}
                    required={!disabled}
                    error={form.formState.errors.summaryTeamsChannelId}
                    helpText={helpText}
                    className={classnames(disabled && 'opacity-50')}
                >
                    <ForceSelectionAutoCompleteField
                        value={hasChannel ? channelLabel(teamName, channelName) : ''}
                        options={options}
                        onSelectOption={(option?: Option | null) => {
                            selectChannel(option);
                            field.onChange(option?.channelId || '');
                        }}
                        renderOption={renderChannelOption}
                        emptyMessage={disabled ? undefined : reason || t('form.summaryTeamsNoChannels')}
                        isLoading={!disabled && loading}
                        disabled={disabled}
                        placeholder={t('form.summaryTeamsPlaceholder')}
                    />
                </FormField>
            )}
        />
    );
};

export default TeamsChannelPicker;
